import { selectors } from "../../../add-element/cypressSelectors";
import { DeepLinkContext } from "../../../contexts/DeepLinkContext";
import { useIsPreviewOrDemo } from "../../../contexts/IsPreviewOrDemoContext";
import API from "../../../core/api";
import ThemeExtractor from "../../../core/components/ThemeExtractor";
import { getGuestTokenCookie } from "../../../core/utils";
import OnlineInvitationRSVPForm, {
  getStoredFormState,
} from "../../../guest-management/forms/RSVPForm/OnlineInvitations/OnlineInvitationRSVPForm";
import RSVPSearchModal from "../../../guest-management/forms/RSVPSearchModal/RSVPSearchModal";
import ConfirmationModal from "../../../minted-components-ports/ConfirmationModal";
import {
  OnlineInvitationSiteDetails,
  RSVPData,
  Site,
  WebsiteEvent,
} from "../../../resources/modelTypes";
import { css } from "@emotion/react";
import { Button } from "@minted/fancyclothes";
import {
  Modal,
  ModalBody,
  ModalHeader,
  styleUtils,
  theme,
} from "@minted/minted-components";
import React, { useCallback, useContext, useEffect, useState } from "react";

const createRsvpStyles = (backgroundColor: string) => ({
  cookieError: css`
    ${theme.typeStyles.bodyMediumStyles};
    color: ${theme.colors.negative800};
    text-align: center;
  `,
  buttonWrapper: css`
    margin-left: ${theme.spacing.x4};
    margin-right: ${theme.spacing.x4};
    position: fixed;
    bottom: ${theme.spacing.x5};
    z-index: 1000;
    button {
      border: ${styleUtils.rem(1)} solid ${backgroundColor};
      width: ${styleUtils.rem(166)};
      box-shadow:
        0 ${styleUtils.rem(4)} ${styleUtils.rem(8)} 0 rgba(0, 0, 0, 0.2),
        0 ${styleUtils.rem(6)} ${styleUtils.rem(20)} 0 rgba(0, 0, 0, 0.19);
    }
    ${theme.media.greaterThan(styleUtils.rem(768))`
      button {
        width: ${styleUtils.rem(200)};
      }
    `};
  `,
  modalHeaderText: css`
    color: ${theme.colors.black};
  `,
});

type OnlineInvitationRSVPButtonProps = {
  event: WebsiteEvent;
  site: Site;
};

const OnlineInvitationRSVPButton = ({
  event,
  site,
}: OnlineInvitationRSVPButtonProps) => {
  const { isPreview } = useIsPreviewOrDemo();
  const isInBrowser = typeof window !== "undefined";
  const hasCookies = isInBrowser && window.navigator.cookieEnabled;
  // Only ever rendered for an online-invitation site (see callers).
  const oiSiteDetails = site.siteDetails as OnlineInvitationSiteDetails;
  const [isRSVPFormOpen, setIsRSVPFormOpen] = useState(false);
  const [isRSVPSearchFormOpen, setIsRSVPSearchFormOpen] = useState(false);
  const [rsvpData, setRSVPData] = useState<RSVPData | null>(null);
  const [existingRsvpAcknowledged, setExistingRsvpAcknowledged] =
    useState(true);

  const { addressbookContactId } = useContext(DeepLinkContext);

  // Load RSVP form data if deep link is provided
  useEffect(() => {
    if (addressbookContactId && !isPreview) {
      const guestToken = getGuestTokenCookie();
      const headers = {
        Authorization: `Guest ${guestToken}`,
      };

      API.get(
        "guest-management/rsvp-form-data",
        {
          headers,
        },
        {
          addressbook_contact_id: addressbookContactId,
        },
      ).then((responseData: RSVPData) => {
        if (responseData.contact) {
          // For OI, event title comes from site details
          responseData.events[0].title = oiSiteDetails.eventHeading;
          setRSVPData(responseData);
          setExistingRsvpAcknowledged(
            !responseData.hasExistingRsvps ||
              Boolean(
                getStoredFormState(
                  responseData.contact.id,
                  responseData.events[0],
                  responseData.guests[0],
                ),
              ),
          );
        }
      });
    }
  }, [
    addressbookContactId,
    isPreview,
    setRSVPData,
    setIsRSVPFormOpen,
    oiSiteDetails.eventHeading,
  ]);

  const setRsvpModalData = useCallback((rsvpData: RSVPData) => {
    setRSVPData(rsvpData);
    setExistingRsvpAcknowledged(
      !rsvpData.hasExistingRsvps ||
        Boolean(
          getStoredFormState(
            rsvpData.contact.id,
            rsvpData.events[0],
            rsvpData.guests[0],
          ),
        ),
    );
    setIsRSVPSearchFormOpen(false);
    setIsRSVPFormOpen(true);
  }, []);

  const exitForm = useCallback(() => {
    setIsRSVPFormOpen(false);
    setRSVPData(null);
    setExistingRsvpAcknowledged(true);
  }, []);

  const goBackToSearch = useCallback(() => {
    setIsRSVPFormOpen(false);
    setRSVPData(null);
    setExistingRsvpAcknowledged(true);
    setIsRSVPSearchFormOpen(true);
  }, []);

  const onRSVPButtonClick = useCallback(() => {
    if (isPreview) {
      return;
    }
    if (rsvpData) {
      setIsRSVPFormOpen(true);
    } else {
      setIsRSVPSearchFormOpen(true);
    }
  }, [isPreview, rsvpData]);

  return (
    <ThemeExtractor rules={["backgroundColor"]}>
      {({ backgroundColor }) => {
        const rsvpStyles = createRsvpStyles(backgroundColor);

        return (
          <>
            <div data-cy="floatingRSVP" css={rsvpStyles.buttonWrapper}>
              {isInBrowser &&
                (hasCookies ? (
                  <Button
                    floating
                    onClick={onRSVPButtonClick}
                    text="RSVP"
                    xPosition="left"
                    yPosition="bottom"
                    disabled={isPreview}
                  />
                ) : (
                  <div
                    css={rsvpStyles.cookieError}
                    data-cy={selectors.rsvp_cookieError}
                  >
                    Cookies are required to submit your RSVP. Please enable
                    cookies in your browser.
                  </div>
                ))}
            </div>
            {isRSVPSearchFormOpen && (
              <RSVPSearchModal
                closeModal={() => setIsRSVPSearchFormOpen(false)}
                configuration={undefined}
                initialSearchTerm=""
                openInvitation={event.openInvitation}
                setRsvpData={setRsvpModalData}
                site={site}
              />
            )}

            {isRSVPFormOpen && rsvpData && !existingRsvpAcknowledged && (
              <ConfirmationModal
                onCloseClick={goBackToSearch}
                onSubmit={() => setExistingRsvpAcknowledged(true)}
                open
                showCancel
                submitButtonText="Open RSVP Form"
                alertPrimaryText="An RSVP was already submitted."
              >
                If you need to change your answers, contact the host or submit
                this form again.
              </ConfirmationModal>
            )}

            {isRSVPFormOpen && rsvpData && existingRsvpAcknowledged && (
              <Modal
                mobileFullscreen
                onCloseClick={exitForm}
                open
                size="medium"
              >
                <ModalHeader>
                  <div css={rsvpStyles.modalHeaderText}>RSVP</div>
                </ModalHeader>
                <ModalBody>
                  <OnlineInvitationRSVPForm
                    contact={rsvpData.contact}
                    event={rsvpData.events[0]}
                    guest={rsvpData.guests[0]}
                  />
                </ModalBody>
              </Modal>
            )}
          </>
        );
      }}
    </ThemeExtractor>
  );
};

export default OnlineInvitationRSVPButton;
